import React, { useState } from 'react';
import ResponseExample from './ResponseExample';
import CodeExample from './CodeExample';
import './ApiTester.css';

/**
 * ApiTester Component
 * Interactive API testing tool for documentation pages
 */
const ApiTester = ({ 
  endpoint = '/splits',
  method = 'GET',
  baseUrl = 'https://us-central1-wesplit-35186.cloudfunctions.net',
  defaultParams = {},
  defaultBody = null,
  description = ''
}) => {
  const [apiKey, setApiKey] = useState('');
  const [params, setParams] = useState(defaultParams);
  const [body, setBody] = useState(defaultBody ? JSON.stringify(defaultBody, null, 2) : '');
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showResponse, setShowResponse] = useState(false);

  const handleParamChange = (key, value) => {
    setParams(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const buildUrl = () => {
    let url = `${baseUrl}${endpoint}`;
    const queryParams = Object.entries(params)
      .filter(([_, value]) => value !== '')
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');
    
    if (queryParams) {
      url += `?${queryParams}`;
    }
    return url;
  };

  const handleTest = async () => {
    setLoading(true);
    setError(null);
    setShowResponse(true);

    try {
      const url = buildUrl();
      const headers = {
        'Content-Type': 'application/json',
      };

      if (apiKey) {
        headers['Authorization'] = `Bearer ${apiKey}`;
      }

      const options = {
        method,
        headers,
      };

      if (method !== 'GET' && method !== 'HEAD' && body) {
        try {
          options.body = JSON.parse(body);
        } catch (e) {
          setError('Invalid JSON in request body');
          setLoading(false);
          return;
        }
      }

      // Simulate API call (replace with actual API call when backend is ready)
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock response based on endpoint
      const mockResponse = getMockResponse(endpoint, method);
      
      setResponse({
        data: mockResponse,
        status: 200,
        statusText: 'OK'
      });
    } catch (err) {
      setError(err.message || 'An error occurred');
      setResponse({
        data: {
          error: {
            code: 'REQUEST_ERROR',
            message: err.message || 'Failed to make request'
          }
        },
        status: 500,
        statusText: 'Internal Server Error'
      });
    } finally {
      setLoading(false);
    }
  };

  const getMockResponse = (endpoint, method) => {
    // Mock responses for different endpoints
    if (endpoint.includes('/auth/login')) {
      return {
        success: true,
        token: 'MOCK_TOKEN_DO_NOT_USE_IN_PRODUCTION',
        expiresIn: 3600,
        user: {
          id: 'user123',
          email: 'user@example.com',
          name: 'John Doe'
        }
      };
    }

    if (endpoint.includes('/splits') && method === 'GET') {
      return {
        data: [
          {
            id: 'split1',
            title: 'Dinner with friends',
            amount: 150.00,
            status: 'pending',
            participants: ['user1', 'user2', 'user3'],
            createdAt: '2025-01-15T10:00:00Z'
          }
        ],
        pagination: {
          page: 1,
          limit: 20,
          total: 1
        }
      };
    }

    if (endpoint.includes('/splits') && method === 'POST') {
      return {
        success: true,
        data: {
          id: 'split_new_123',
          title: JSON.parse(body || '{}').title || 'New Split',
          amount: JSON.parse(body || '{}').amount || 0,
          status: 'pending',
          participants: JSON.parse(body || '{}').participants || [],
          createdAt: new Date().toISOString()
        }
      };
    }

    if (endpoint.includes('/transactions')) {
      return {
        data: [
          {
            id: 'tx1',
            splitId: 'split1',
            amount: 50.00,
            status: 'completed',
            currency: 'USD',
            createdAt: '2025-01-15T10:00:00Z'
          }
        ]
      };
    }

    if (endpoint.includes('/wallets')) {
      return {
        data: [
          {
            id: 'wallet1',
            name: 'Vacation Fund',
            balance: 500.00,
            members: ['user1', 'user2'],
            createdAt: '2025-01-10T10:00:00Z'
          }
        ]
      };
    }

    return {
      success: true,
      message: 'Request successful',
      data: {}
    };
  };

  const generateCurlCommand = () => {
    const url = buildUrl();
    let curl = `curl -X ${method} "${url}"`;
    
    // Always use placeholder instead of actual API key for security
    curl += ` \\\n  -H "Authorization: Bearer YOUR_API_KEY"`;
    
    curl += ` \\\n  -H "Content-Type: application/json"`;
    
    if (method !== 'GET' && method !== 'HEAD' && body) {
      curl += ` \\\n  -d '${body.replace(/\n/g, ' ')}'`;
    }
    
    return curl;
  };

  const generateFetchCode = () => {
    const url = buildUrl();
    let code = `fetch('${url}', {\n`;
    code += `  method: '${method}',\n`;
    code += `  headers: {\n`;
    // Always use placeholder instead of actual API key for security
    code += `    'Authorization': 'Bearer YOUR_API_KEY',\n`;
    code += `    'Content-Type': 'application/json'\n`;
    code += `  }`;
    
    if (method !== 'GET' && method !== 'HEAD' && body) {
      try {
        const parsedBody = JSON.parse(body);
        const bodyStr = JSON.stringify(parsedBody, null, 2);
        const bodyLines = bodyStr.split('\n');
        const formattedBody = bodyLines.map((line, idx) => {
          return idx === 0 ? line : `    ${line}`;
        }).join('\n');
        code += `,\n  body: JSON.stringify(${formattedBody})`;
      } catch {
        code += `,\n  body: ${JSON.stringify(body)}`;
      }
    }
    
    code += `\n})\n`;
    code += `.then(response => response.json())\n`;
    code += `.then(data => console.log(data));`;
    
    return code;
  };

  return (
    <div className="api-tester">
      <div className="api-tester-header">
        <h3 className="api-tester-title">Try It Out</h3>
        {description && <p className="api-tester-description">{description}</p>}
      </div>

      <div className="api-tester-config">
        <div className="api-tester-warning">
          ⚠️ <strong>Mock Mode:</strong> This tester returns simulated responses. Do not enter real API keys. Generated code examples are for reference only.
        </div>
        <div className="api-tester-field">
          <label className="api-tester-label">API Key (Optional - For Testing Only)</label>
          <input
            type="password"
            className="api-tester-input"
            placeholder="Enter your API key (will not be used in generated code)"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
          <p className="api-key-input-warning">Note: API keys entered here are not included in generated code examples for security.</p>
        </div>

        {Object.keys(params).length > 0 && (
          <div className="api-tester-params">
            <label className="api-tester-label">Parameters</label>
            {Object.entries(params).map(([key, value]) => (
              <div key={key} className="api-tester-param-row">
                <span className="api-tester-param-key">{key}</span>
                <input
                  type="text"
                  className="api-tester-input api-tester-param-input"
                  placeholder={key}
                  value={value}
                  onChange={(e) => handleParamChange(key, e.target.value)}
                />
              </div>
            ))}
          </div>
        )}

        {(method === 'POST' || method === 'PUT' || method === 'PATCH') && (
          <div className="api-tester-field">
            <label className="api-tester-label">Request Body (JSON)</label>
            <textarea
              className="api-tester-textarea"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder='{"key": "value"}'
              rows={6}
            />
          </div>
        )}

        <button
          className="api-tester-button"
          onClick={handleTest}
          disabled={loading}
        >
          {loading ? 'Testing...' : 'Test Endpoint'}
        </button>
      </div>

      {showResponse && (
        <div className="api-tester-response">
          {error && (
            <div className="api-tester-error">
              <strong>Error:</strong> {error}
            </div>
          )}

          {response && (
            <>
              <div className="api-tester-response-header">
                <h4>Response</h4>
                <span className={`api-tester-status api-tester-status-${Math.floor(response.status / 100)}xx`}>
                  {response.status} {response.statusText}
                </span>
              </div>
              
              <ResponseExample
                response={response.data}
                statusCode={response.status}
                title="Response Body"
              />
            </>
          )}
        </div>
      )}

      <div className="api-tester-code-examples">
        <h4 className="api-tester-code-title">Code Examples</h4>
        <CodeExample
          code={generateCurlCommand()}
          language="bash"
          title="cURL"
        />
        <CodeExample
          code={generateFetchCode()}
          language="javascript"
          title="JavaScript (Fetch)"
        />
      </div>
    </div>
  );
};

export default ApiTester;
